import type { RepeatConfig, RepeatType, Line } from "@/lib/types";

// ---- ローカル日付文字列(YYYY-MM-DD)ユーティリティ ----
// toISOString() は UTC 基準で JST 早朝に前日になるため使わない

function toDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function parseDateStr(s: string): Date {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function addDays(s: string, days: number): string {
  const d = parseDateStr(s);
  d.setDate(d.getDate() + days);
  return toDateStr(d);
}

function daysInMonth(year: number, month1: number): number {
  return new Date(year, month1, 0).getDate();
}

function makeDateStr(year: number, month1: number, day: number): string {
  return `${year}-${String(month1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/**
 * 次回発生日を返す。ルールは「todayStr より後で、パターンに合う最初の日付」。
 * 期限切れタスクを遅れて完了しても過去分を量産しない。
 * 不正な設定(空の daysOfWeek、anchor 欠落、未対応 type)は null。
 */
export function nextOccurrence(repeat: RepeatConfig, todayStr: string): string | null {
  switch (repeat.type) {
    case "daily":
      return addDays(todayStr, 1);

    case "weekly": {
      const days = (repeat.daysOfWeek ?? []).filter((d) => Number.isInteger(d) && d >= 0 && d <= 6);
      if (days.length === 0) return null;
      for (let i = 1; i <= 7; i++) {
        const candidate = addDays(todayStr, i);
        if (days.includes(parseDateStr(candidate).getDay())) return candidate;
      }
      return null;
    }

    case "monthly": {
      const anchorDay = repeat.anchorDay;
      if (!anchorDay || anchorDay < 1 || anchorDay > 31) return null;
      const today = parseDateStr(todayStr);
      // 当月 → 翌月 → 翌々月の順に「今日より後」の候補を探す
      for (let offset = 0; offset <= 2; offset++) {
        const m0 = today.getMonth() + offset; // 0-indexed で加算
        const year = today.getFullYear() + Math.floor(m0 / 12);
        const month1 = (m0 % 12) + 1;
        const day = Math.min(anchorDay, daysInMonth(year, month1));
        const candidate = makeDateStr(year, month1, day);
        if (candidate > todayStr) return candidate;
      }
      return null;
    }

    case "yearly": {
      const { anchorMonth, anchorDay } = repeat;
      if (!anchorMonth || anchorMonth < 1 || anchorMonth > 12) return null;
      if (!anchorDay || anchorDay < 1 || anchorDay > 31) return null;
      const today = parseDateStr(todayStr);
      for (let offset = 0; offset <= 1; offset++) {
        const year = today.getFullYear() + offset;
        const day = Math.min(anchorDay, daysInMonth(year, anchorMonth));
        const candidate = makeDateStr(year, anchorMonth, day);
        if (candidate > todayStr) return candidate;
      }
      return null;
    }

    default:
      // custom は未対応(スペックのスコープ外)
      return null;
  }
}

/**
 * UI の選択(プリセット + 基準日)から RepeatConfig を構築する。
 * weekly で曜日未指定なら基準日の曜日を使う。
 */
export function buildRepeatConfig(
  type: RepeatType,
  baseDateStr: string,
  daysOfWeek?: number[]
): RepeatConfig | null {
  switch (type) {
    case "daily":
      return { type: "daily", interval: 1 };
    case "weekly": {
      const days = daysOfWeek && daysOfWeek.length > 0
        ? [...daysOfWeek].sort((a, b) => a - b)
        : [parseDateStr(baseDateStr).getDay()];
      return { type: "weekly", interval: 1, daysOfWeek: days };
    }
    case "monthly":
      return { type: "monthly", interval: 1, anchorDay: parseDateStr(baseDateStr).getDate() };
    case "yearly": {
      const d = parseDateStr(baseDateStr);
      return { type: "yearly", interval: 1, anchorMonth: d.getMonth() + 1, anchorDay: d.getDate() };
    }
    default:
      return null;
  }
}

// ---- 補助関数 ----

const REPEAT_TYPES: RepeatType[] = ["daily", "weekly", "monthly", "yearly", "custom"];
const WEEKDAY_LABELS = ["日", "月", "火", "水", "木", "金", "土"];

/**
 * DB の JSONB 値を RepeatConfig として安全に解釈する。
 * 不正な値は undefined(クラッシュさせない)。
 */
export function parseRepeatFromRow(value: unknown): RepeatConfig | undefined {
  let obj: unknown = value;
  if (typeof value === "string") {
    try {
      obj = JSON.parse(value);
    } catch {
      return undefined;
    }
  }
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return undefined;
  const rec = obj as Record<string, unknown>;
  if (!REPEAT_TYPES.includes(rec.type as RepeatType)) return undefined;
  if (typeof rec.interval !== "number") return undefined;
  return rec as unknown as RepeatConfig;
}

/**
 * RepeatConfig の内容比較。sync-manager の差分検出で参照比較の代わりに使う。
 * buildRepeatConfig で構築するためキー順は安定しており JSON 比較で十分。
 */
export function repeatConfigsEqual(a?: RepeatConfig, b?: RepeatConfig): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

/** UI 表示用ラベル */
export function repeatLabel(repeat: RepeatConfig | undefined): string {
  if (!repeat) return "繰り返しなし";
  switch (repeat.type) {
    case "daily":
      return "毎日";
    case "weekly": {
      const days = (repeat.daysOfWeek ?? []).map((d) => WEEKDAY_LABELS[d]).filter(Boolean);
      return days.length > 0 ? `毎週 ${days.join("・")}` : "毎週";
    }
    case "monthly":
      return repeat.anchorDay ? `毎月${repeat.anchorDay}日` : "毎月";
    case "yearly":
      return repeat.anchorMonth && repeat.anchorDay
        ? `毎年${repeat.anchorMonth}月${repeat.anchorDay}日`
        : "毎年";
    default:
      return "繰り返し";
  }
}

/** YYYY-MM-DD → "M/D" */
export function formatMonthDay(dateStr: string): string {
  const [, m, d] = dateStr.split("-").map(Number);
  return `${m}/${d}`;
}

/**
 * 完了した繰り返しタスクから次回分の Line を構築する。
 * id / now は呼び出し側が生成して渡す(React の setState updater 内で
 * 非決定値を作らないため、および純関数としてテスト可能にするため)。
 */
export function buildNextOccurrenceLine(
  task: Line,
  todayStr: string,
  gen: { id: string; now: number }
): Line | null {
  if (!task.repeat || task.type !== "task") return null;
  const next = nextOccurrence(task.repeat, todayStr);
  if (!next) return null;
  return {
    id: gen.id,
    type: "task",
    content: task.content,
    status: "not-started",
    createdAt: gen.now,
    scheduledDate: next,
    repeat: task.repeat,
    ...(task.reminder !== undefined ? { reminder: task.reminder } : {}),
    ...(task.priority !== undefined ? { priority: task.priority } : {}),
    ...(task.tags && task.tags.length > 0 ? { tags: [...task.tags] } : {}),
    ...(task.notes ? { notes: task.notes } : {}),
    ...(task.notebookId ? { notebookId: task.notebookId } : {}),
    ...(task.indentLevel !== undefined ? { indentLevel: task.indentLevel } : {}),
    ...(task.bulletPoint !== undefined ? { bulletPoint: task.bulletPoint } : {}),
    ...(task.assignee !== undefined ? { assignee: task.assignee } : {}),
    ...(task.assigneeUserId !== undefined ? { assigneeUserId: task.assigneeUserId } : {}),
  };
}
